import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { modalStyles } from '../../utils/modalStyles';
import { BulkApprovalResult } from '../../services/firestoreChannelService';

interface BulkApprovalResultsModalProps {
  open: boolean;
  result: BulkApprovalResult | null;
  onClose: () => void;
}

export const BulkApprovalResultsModal: React.FC<BulkApprovalResultsModalProps> = ({
  open,
  result,
  onClose,
}) => {
  if (!result) {
    return null;
  }

  const totalProcessed = result.approved + result.failed;
  const approvalRate = totalProcessed > 0 ? (result.approved / totalProcessed) * 100 : 0;
  const hasErrors = result.failed > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={modalStyles.dialog}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        Bulk Approval Results
      </DialogTitle>

      <DialogContent sx={modalStyles.dialogContent}>
        <Stack spacing={3}>
          {/* Summary Section */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
            <Stack spacing={2}>
              {/* Progress Bar */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Overall Success Rate</Typography>
                  <Typography variant="subtitle2" color={hasErrors ? 'error' : 'success'}>
                    {approvalRate.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={approvalRate}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: 'divider',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: hasErrors ? '#ff9800' : '#4caf50',
                    },
                  }}
                />
              </Box>

              {/* Stats */}
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                    ✓ APPROVED
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {result.approved}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                    ✗ FAILED
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {result.failed}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                    TOTAL
                  </Typography>
                  <Typography variant="h5" color="textSecondary">
                    {totalProcessed}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Error Details */}
          {result.errors.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {result.errors.length} Channel(s) Failed
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'error.lighter' }}>
                      <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>Channel ID</TableCell>
                      <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((error, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {error.channel_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{error.error}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Success Message */}
          {result.failed === 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              All channels have been successfully approved!
            </Alert>
          )}

          {/* Partial Success Message */}
          {result.failed > 0 && result.approved > 0 && (
            <Alert severity="warning">
              {result.approved} of {totalProcessed} channels were approved. Please address the errors above
              and retry the failed channels.
            </Alert>
          )}

          {/* All Failed Message */}
          {result.approved === 0 && result.failed > 0 && (
            <Alert severity="error">
              All channels failed approval. Please review the errors and address any issues before
              retrying.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={modalStyles.dialogActions}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkApprovalResultsModal;
